import { isMail } from 'components/mail/scheduleSlots';
import { formatPhone, phoneErrorCode } from 'helper/prospectPhone';
import { DEFAULT_PROSPECT_SOURCE } from 'helper/prospectOptions';

const DIRECTORY_FIELDS = [
  'reasonSocial',
  'siret',
  'legalForm',
  'codeApe',
  'idcc',
  'staff',
  'activity',
];

export const emptyProspectForm = () => ({
  name: '',
  // The AddressGeoApi value, or null until an address is picked.
  address: null,
  phone: '',
  // The company's general address (crmProspect.email), sent when filled.
  email: '',
  contacts: [],
  pennylaneCategoryId: '',
  source: DEFAULT_PROSPECT_SOURCE,
  // The commercial in charge: the caller when a commercial, else chosen.
  commercialId: '',
});

const ADDRESS_TEXT_FIELDS = [
  'street',
  'complement',
  'postcode',
  'city',
  'department',
];

const trimmed = (value) => String(value ?? '').trim();

// `address` is the AddressGeoApi value: a picked suggestion (its dataset, all
// strings, the department being the BAN context « 972, Martinique »), the
// manual form, or the directory location. The api turns the department into
// its name.
const buildAddress = (address) => {
  if (!address) return null;
  const result = {};
  ADDRESS_TEXT_FIELDS.forEach((key) => {
    const value = trimmed(address[key]);
    if (value) result[key] = value;
  });
  ['lat', 'lon'].forEach((key) => {
    if (trimmed(address[key]) === '') return;
    const value = Number(address[key]);
    if (Number.isFinite(value)) result[key] = value;
  });
  return Object.keys(result).length ? result : null;
};

export const hasAddressDepartment = (address) =>
  Boolean(trimmed(address?.department));

export const isProspectFormValid = ({ company, form }) => {
  if (!company || company.followed) return false;
  if (!String(form.name ?? '').trim()) return false;
  if (!hasAddressDepartment(form.address)) return false;
  if (phoneErrorCode(form.phone)) return false;
  if (String(form.email ?? '').trim() && !isMail(form.email)) return false;
  return (form.contacts ?? []).every(
    (contact) =>
      !String(contact.phone ?? '').trim() || !phoneErrorCode(contact.phone),
  );
};

// `today` ({ sessionId }) puts the prospect in the caller's call list of the
// day, in that day session when there is one.
export const buildProspectPayload = ({ company, form, today = null }) => {
  const fields = {
    name: String(form.name ?? '').trim() || company.name,
  };
  if (!company.manual) {
    DIRECTORY_FIELDS.forEach((key) => {
      // A staff of 0 (INSEE band '00') is a real value.
      if (
        company[key] !== undefined &&
        company[key] !== null &&
        company[key] !== ''
      ) {
        fields[key] = company[key];
      }
    });
    if (company.leader) fields.leader = company.leader;
  }
  const address = buildAddress(form.address);
  if (address) fields.address = address;
  const categoryId = Number(form.pennylaneCategoryId);
  if (Number.isInteger(categoryId) && categoryId > 0) {
    fields.pennylaneCategoryId = categoryId;
  }
  fields.source = form.source;
  if (form.commercialId) fields.commercialId = form.commercialId;
  fields.phone = formatPhone(form.phone);
  const email = String(form.email ?? '').trim().toLowerCase();
  if (email) fields.email = email;
  fields.contacts = (form.contacts ?? [])
    .filter(
      (contact) =>
        String(contact.name ?? '').trim() || String(contact.phone ?? '').trim(),
    )
    .map((contact) => ({
      name: String(contact.name ?? '').trim(),
      position: String(contact.position ?? '').trim(),
      phone: String(contact.phone ?? '').trim()
        ? formatPhone(contact.phone)
        : '',
      email: String(contact.email ?? '').trim(),
    }));
  if (!today) return { fields };
  return today.sessionId
    ? { fields, addToday: true, sessionId: today.sessionId }
    : { fields, addToday: true };
};
